/**
 * Subscribable mirror of the SDK's opt-in state.
 *
 * `has_opted_out_capturing()` can only be polled, but capture turns on
 * asynchronously — `usePostHogCompanyGating` opts in only once the active
 * company's detail resolves. So anything capturing on mount fires into a closed
 * gate, and nothing re-renders it when the gate opens.
 *
 * The writers are the gating hook and the logout middleware, the only two
 * places that call `opt_in_capturing` / `opt_out_capturing`. A third writer
 * that forgot to mirror here would leave tracked views waiting on a gate that
 * is already open.
 */

let active = false;
const listeners = new Set<() => void>();

export function setAnalyticsActive(next: boolean): void {
  if (active === next) {
    return;
  }
  active = next;
  for (const listener of listeners) {
    listener();
  }
}

export function getAnalyticsActive(): boolean {
  return active;
}

export function subscribeAnalyticsActive(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/** Test-only. Leaves `listeners` alone — a mounted subscription outlives it. */
export function resetAnalyticsActiveForTests(): void {
  setAnalyticsActive(false);
}
